import { mapValues, memoize } from '@exodus/basic-utils'
import { hash } from '@exodus/crypto/hash'
import { randomBytes } from '@exodus/crypto/randomBytes'
import { genSignKeyPair } from '@exodus/crypto/sodium-compat'
import BJSON from 'buffer-json'
import pDefer from 'p-defer'

import ABTestingClient from './client.js'

const hashEventType = memoize((value) => hash('sha256', value, 'hex')) // async

class ABTestingModule {
  #fusion
  #abTestingClient
  #abTestingAtom
  #abTestingLoadedAtom
  #getBuildMetadata
  #loaded = pDefer()

  constructor({ fusion, abTestingAtom, abTestingLoadedAtom, getBuildMetadata, logger, config }) {
    const { apiUrl } = config

    if (!apiUrl) {
      throw new Error('`apiUrl` config is required')
    }

    this.#fusion = fusion
    this.#abTestingAtom = abTestingAtom
    this.#abTestingLoadedAtom = abTestingLoadedAtom
    this.#getBuildMetadata = getBuildMetadata
    this.#abTestingClient = new ABTestingClient({ apiUrl, logger })
  }

  #getSigningKeyPair = async () => {
    const profile = await this.#fusion.getProfile()
    const abTestingKeyPair = profile.private.abTestingKeyPair
    return abTestingKeyPair ? BJSON.parse(abTestingKeyPair) : null
  }

  #generateSigningKeyPair = async () => {
    const keyPair = await genSignKeyPair(randomBytes(32))
    const abTestingKeyPair = BJSON.stringify(keyPair)

    await this.#fusion.mergeProfile({ private: { abTestingKeyPair } })

    return keyPair
  }

  #loadVariants = async () => {
    const { variants: currentVariants } = await this.#abTestingAtom.get()
    let variantsData
    try {
      variantsData = await this.#abTestingClient.getVariants()
    } catch (error) {
      return {
        value: currentVariants,
        error: 'failed to request variants:' + error.message,
      }
    }

    const { value: remoteVariants } = variantsData
    return {
      value: mapValues(remoteVariants, (remoteVariant, experiment) => {
        const currentVariant = currentVariants[experiment]
        const hasLocalVariant = currentVariant?.type === 'local'

        return hasLocalVariant
          ? { type: 'local', value: currentVariant.value }
          : { type: 'remote', value: remoteVariant }
      }),
    }
  }

  #loadExperiments = async () => {
    try {
      return await this.#abTestingClient.getExperiments()
    } catch (e) {
      const { experiments } = await this.#abTestingAtom.get()
      return { value: experiments, error: 'failed to request experiments:' + e.message }
    }
  }

  load = async () => {
    let signingKeyPair = await this.#getSigningKeyPair()

    if (!signingKeyPair) {
      signingKeyPair = await this.#generateSigningKeyPair()
    }

    const metadata = await this.#getBuildMetadata()

    this.#abTestingClient.authenticate(signingKeyPair, metadata)

    const [variantsData, experimentsData] = await Promise.all([
      this.#loadVariants(),
      this.#loadExperiments(),
    ])
    const { value: variants, error: variantsError } = variantsData
    const { value: experiments, error: experimentsError } = experimentsData
    const errors =
      variantsError || experimentsError
        ? { variants: variantsError, experiments: experimentsError }
        : undefined

    await this.#abTestingAtom.set({ variants, experiments, errors })
    await this.#abTestingLoadedAtom.set(true)

    this.#loaded.resolve()
  }

  getVariant = async (experimentId) => {
    await this.#loaded.promise

    const { experiments, variants } = await this.#abTestingAtom.get()
    if (!experiments?.[experimentId]?.enabled) return

    return variants?.[experimentId]?.value
  }

  updateVariant = async ({ experimentId, value }) => {
    await this.#loaded.promise

    const { experiments, variants } = await this.#abTestingAtom.get()

    await this.#abTestingAtom.set({
      experiments,
      variants: { ...variants, [experimentId]: { value, type: 'local' } },
    })
  }

  trackEvent = async (event) => {
    await this.#loaded.promise

    const type = await hashEventType(event.type)

    await this.#abTestingClient.trackEvent({ ...event, type })
  }

  clear = async () => {
    await this.#abTestingAtom.set(undefined)
    await this.#abTestingLoadedAtom.set(false)
  }
}

const createABTesting = (args) => new ABTestingModule(args)

const abTestingDefintion = {
  id: 'abTesting',
  type: 'module',
  factory: createABTesting,
  dependencies: [
    'fusion',
    'abTestingAtom',
    'getBuildMetadata',
    'abTestingLoadedAtom',
    'logger',
    'config',
  ],
  public: true,
}

export default abTestingDefintion
